import styled from "@emotion/styled";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useColorScheme
} from "@mui/material";
import { ReasonType } from "@rooster/types";
import { getCountryForTimezone } from "countries-and-timezones";
import { Formik } from "formik";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { validatePhoneNumber } from "@rooster/utils";

import { MdOutlineMailOutline, MdOutlinePerson, MdPhone } from "react-icons/md";
import { validateEmailUsingRegex } from "../../constants/common";
import { useAnnouncer } from "../../hooks/useAnnouncer";
import { useCandidateReasons } from "../../hooks/useCandidateReasons";
import { useSchedulerStore } from "../../state/scheduler.store";
import {
  actionBarButtonPrimaryDarkStyle,
  actionBarButtonPrimaryLightStyle
} from "../../styles";
import { BookingFormType, LegalType } from "../../types";
import { focusRingStyles } from "../../utils/accessibility";
import { TermsAndConditionsPopup } from "../Popups/TermsAndConditionsPopup";

const PhoneInputStyled = styled(PhoneInput)<{ theme: "light" | "dark" }>`
  .form-control {
    width: 100% !important;
    font-size: 14px !important;
    border-radius: 10px !important;
    border-color: ${({ theme }) => (theme === "light" ? "#bdbdbd" : "#5a5a5a")} !important;
    background: transparent;
    color:${({ theme }) => (theme === "light" ? "#202020" : "#ffffff")} !important; ;
    border-radius: 5px !important;
  }

  .form-control:hover {
    border-color: ${({ theme }) => (theme === "light" ? "#bdbdbd" : "#5a5a5a")} !important;
  }

  .form-control:disabled {
    color: #959595;
  }

  .form-control:focus {
    border-color: #145b99 !important;
    border-width: 2px !important;
    outline: none !important;
    background: transparent !important; 
    box-shadow: none !important;
  }

  .form-control {
    padding: 12px 14px 12px 60px;
  }

  .country-list .country .country-name,
  .country-list .country.highlight .country-name {
    color: #333333;
  }

  .country-list .country .dial-code,
  .country-list .country.highlight .dial-code {
    color: #595959;
  }
`;

const detectUserCountryFromCurrentTimezone = (fallbackCountryCode: string) => {
  return (
    getCountryForTimezone(moment.tz.guess())?.id.toLowerCase() ??
    fallbackCountryCode.toLowerCase()
  );
};

// Helper component to announce form errors to screen readers
const ErrorAnnouncer: React.FC<{
  errors: Record<string, string>;
  announce: (message: string, priority?: "polite" | "assertive") => void;
}> = ({ errors, announce }) => {
  useEffect(() => {
    const errorMessages = Object.values(errors).filter(Boolean);
    if (errorMessages.length > 0) {
      // Announce the first error for immediate feedback
      announce(errorMessages[0] as string, "assertive");
    }
  }, [errors, announce]);

  return null;
};

interface IBookingForm {
  formType: "Standalone" | "Prospect";
  savedFormData?: BookingFormType;
  legal: LegalType[];
  candidateRequiredFields?: string[];
  isReschedule: boolean;
  isAutobook?: boolean;
  submitHandler: (
    bookingFormData: BookingFormType,
    legal: LegalType[],
    selectedLanguages: Record<string, string>
  ) => void;
  backHandler: () => void;
}

export const BookingForm: React.FC<IBookingForm> = ({
  formType,
  legal,
  savedFormData,
  isReschedule,
  candidateRequiredFields = [],
  isAutobook = false,
  submitHandler,
  backHandler
}) => {
  const { mode } = useColorScheme();
  const { t } = useTranslation();
  const { announce } = useAnnouncer();
  const [TnCPopupState, setTnCPopupState] = useState(false);
  // Reactive on `legal` so async-populated T&C (fetched into the scheduler
  // store after mount) correctly surfaces the checkbox. Mirrors the
  // `useMemo` pattern already used in `SubmitAvailabilityDialog`.
  const enableTnCState = legal.length > 0;
  const [savedFormDataAvailable] = useState(!!savedFormData?.firstName);
  const [selectedLanguages, setSelectedLanguages] = useState<
    Record<string, string>
  >({});

  const phoneA11yObserverRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    return () => {
      phoneA11yObserverRef.current?.disconnect();
    };
  }, []);

  const phoneContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const fixPhoneA11y = () => {
      const countryList = node.querySelector(".country-list");
      if (countryList) {
        countryList.removeAttribute("role");
        countryList.setAttribute("aria-label", "Country");
      }
      node.querySelectorAll(".country-list li.country").forEach((li) => {
        li.removeAttribute("role");
        li.removeAttribute("aria-selected");
      });
      const searchBox = node.querySelector(".search-box");
      if (searchBox) {
        searchBox.setAttribute("aria-label", "Search country");
      }
      const selectedFlag = node.querySelector(".selected-flag");
      if (selectedFlag) {
        selectedFlag.setAttribute("aria-label", "Select country");
        if (selectedFlag.getAttribute("aria-haspopup") === "listbox") {
          selectedFlag.setAttribute("aria-haspopup", "true");
        }
      }
    };
    fixPhoneA11y();
    phoneA11yObserverRef.current?.disconnect();
    const observer = new MutationObserver(fixPhoneA11y);
    observer.observe(node, { childList: true, subtree: true });
    phoneA11yObserverRef.current = observer;
  }, []);

  const link = useSchedulerStore((state) => state.link);
  const sessionData = useSchedulerStore((state) => state.sessionData);
  const storeActions = useSchedulerStore((state) => state.actions);

  const {
    reasons: rescheduleReasons,
    isRequired: isRescheduleRequired,
    showComment: showRescheduleComment,
    loading: rescheduleReasonsLoading
  } = useCandidateReasons({
    availabilityId: link?.scheduleId ?? null,
    operation: "reschedule",
    // Reasons apply to autobook interviews only — never RoosterLinks/prospect.
    enabled: isReschedule && isAutobook
  });

  const [selectedReasonId, setSelectedReasonId] = useState<string>("");
  const [rescheduleComment, setRescheduleComment] = useState("");

  const showRescheduleReasonFields =
    isReschedule && rescheduleReasons.length > 0;

  const selectedReason = rescheduleReasons.find(
    (r) => r.id === selectedReasonId
  );

  const updateStoreReason = (reason?: ReasonType, comment?: string) => {
    storeActions.setSessionData({
      ...sessionData,
      rescheduleReason: reason || comment ? { reason, comment } : undefined
    });
  };

  const handleReasonChange = (reasonId: string) => {
    setSelectedReasonId(reasonId);
    const reason = rescheduleReasons.find((r) => r.id === reasonId);
    updateStoreReason(reason, rescheduleComment || undefined);
  };

  const handleCommentChange = (value: string) => {
    const trimmed = value.slice(0, 500);
    setRescheduleComment(trimmed);
    updateStoreReason(selectedReason, trimmed || undefined);
  };

  // The comment can only be typed while the box is visible, so this is belt and
  // braces: if the tenant setting ever turns the box off mid-session, drop what
  // was already typed instead of submitting a comment the candidate can no
  // longer see or edit.
  useEffect(() => {
    if (showRescheduleComment) return;
    if (!rescheduleComment && !sessionData?.rescheduleReason?.comment) return;

    setRescheduleComment("");
    storeActions.setSessionData({
      ...sessionData,
      rescheduleReason: sessionData?.rescheduleReason?.reason
        ? { reason: sessionData.rescheduleReason.reason }
        : undefined
    });
  }, [showRescheduleComment, rescheduleComment, sessionData, storeActions]);

  const isValidLinkedInUrl = (linkedInUrl: string) => {
    const linkedinUrlRegex = new RegExp(
      /http(s)?:\/\/?(\w{0,3}\.)?linkedin\.com\/(pub|in|profile)/gm
    );
    return linkedinUrlRegex.test(linkedInUrl);
  };

  const isAttendeePhoneRequired = candidateRequiredFields?.includes("phone");

  const validateBookingFormPhone = (inputNumber: string) => {
    // Trim whitespace and handle empty or missing input
    const trimmed = inputNumber?.trim() || "";
    if (!trimmed || trimmed.length === 0) {
      // Empty is valid only if phone is not required
      return !isAttendeePhoneRequired;
    }
    // Validate non-empty phone numbers
    return validatePhoneNumber(`+${trimmed}`);
  };

  const validateFormikForm = (values) => {
    const errors: Record<string, string> = {};

    if (isAutobook) {
      return errors;
    }

    if (!values.email) {
      errors.email = t("ROOSTER.REASONS-TAB.REQUIRED", {
        defaultValue: "Required"
      });
    } else if (values.email.length > 50) {
      errors.email = t("ROOSTER.REASONS-TAB.MAX-CHARACTER-LIMIT-IS-50", {
        defaultValue: "Max character limit is 50"
      });
    } else if (
      values.email?.includes("@") &&
      values.email?.split("@")?.[1]?.includes(".")
    ) {
      if (!validateEmailUsingRegex(values.email)) {
        errors.email = t("ROOSTER.REASONS-TAB.INVALID-EMAIL-ADDRESS", {
          defaultValue: "Invalid email address"
        });
      }
    } else {
      errors.email = t("ROOSTER.REASONS-TAB.INVALID-EMAIL-ADDRESS", {
        defaultValue: "Invalid email address"
      });
    }

    if (!values.firstName) {
      errors.firstName = t("ROOSTER.REASONS-TAB.REQUIRED", {
        defaultValue: "Required"
      });
    }

    if (values.firstName.length > 20) {
      errors.firstName = t("ROOSTER.REASONS-TAB.MAX-CHARACTER-LIMIT-IS-20", {
        defaultValue: "Max character limit is 20"
      });
    }

    if (values?.lastName?.length === 0) {
      errors.lastName = t("ROOSTER.REASONS-TAB.REQUIRED", {
        defaultValue: "Required"
      });
    }

    if (values.lastName.length > 20) {
      errors.lastName = t("ROOSTER.REASONS-TAB.MAX-CHARACTER-LIMIT-IS-20", {
        defaultValue: "Max character limit is 20"
      });
    }

    if (isAttendeePhoneRequired && !values?.telephone) {
      errors.telephone = t("ROOSTER.REASONS-TAB.REQUIRED", {
        defaultValue: "Required"
      });
    } else if (values?.telephone?.length > 0) {
      if (!validateBookingFormPhone(`${values.telephone}`)) {
        errors.telephone = t("ROOSTER.REASONS-TAB.INVALID-PHONE-NUMBER", {
          defaultValue: "Invalid phone number"
        });
      }
    }

    if (
      !isValidLinkedInUrl(values.linkedin.toLowerCase()) &&
      values.linkedin.length
    ) {
      errors.linkedin = t("ROOSTER.REASONS-TAB.INVALID-LINKEDIN-URL", {
        defaultValue: "Invalid linkedin url"
      });
    }

    return errors;
  };

  return (
    <Formik
      initialValues={{
        firstName: savedFormData?.firstName ?? "",
        lastName: savedFormData?.lastName ?? "",
        email: savedFormData?.email ?? "",
        telephone: savedFormData?.telephone ?? "",
        jobRef: savedFormData?.jobRef ?? "",
        bookingNote: savedFormData?.bookingNote ?? "",
        linkedin: savedFormData?.linkedin ?? "",
        termsAndConditions: false
      }}
      isInitialValid={savedFormDataAvailable}
      validateOnMount={savedFormDataAvailable}
      validate={validateFormikForm}
      onSubmit={async (values, { setSubmitting }) => {
        await submitHandler(values, legal, selectedLanguages);
        setSubmitting(false);
      }}
    >
      {({
        values,
        isSubmitting,
        errors,
        isValid,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue
      }) => (
        <Stack
          justifyItems="center"
          alignItems="flex-start"
          alignContent="center"
          spacing={"14px"}
          height="100%"
          width="100%"
          justifyContent="space-between"
        >
          {/* Announce form errors to screen readers */}
          <ErrorAnnouncer errors={errors} announce={announce} />
          <TermsAndConditionsPopup
            legal={legal}
            open={TnCPopupState}
            onClose={() => {
              setTnCPopupState(false);
            }}
            selectedLanguages={selectedLanguages}
            onSelectedLanguagesChange={setSelectedLanguages}
          />
          <Stack direction="column" spacing={2} height="100%" width="100%">
            <Stack direction="row" width="100%" spacing={2}>
              <Stack flex={1} data-testid="first-name-field-container">
                <FormLabel
                  htmlFor="firstName"
                  sx={{ fontWeight: "bold", fontSize: "14px" }}
                  required={!isAutobook}
                  data-testid="first-name-label"
                >
                  {t("ROOSTER.COMMON.FIRST-NAME", {
                    defaultValue: "First Name"
                  })}
                </FormLabel>
                {!isAutobook ? (
                  <TextField
                    fullWidth
                    id="firstName"
                    name="firstName"
                    placeholder="John"
                    variant="outlined"
                    size="small"
                    onChange={handleChange}
                    value={values.firstName}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    disabled={isReschedule || isAutobook}
                    slotProps={{
                      htmlInput: {
                        "aria-describedby": errors.firstName
                          ? "firstName-error"
                          : undefined,
                        "aria-invalid": !!errors.firstName,
                        "aria-required": !isAutobook,
                        "data-testid": "first-name-input-field"
                      },
                      formHelperText: {
                        id: "firstName-error",
                        role: errors.firstName ? "alert" : undefined
                      }
                    }}
                    sx={focusRingStyles}
                    data-testid="first-name-input"
                  />
                ) : (
                  <Typography
                    fontWeight={500}
                    pt={1}
                    fontSize={16}
                    alignItems="center"
                    display="flex"
                    sx={{ gap: "5px" }}
                  >
                    <MdOutlinePerson /> {values.firstName}
                  </Typography>
                )}
              </Stack>
              <Stack flex={1} data-testid="last-name-field-container">
                <FormLabel
                  htmlFor="lastName"
                  sx={{ fontWeight: "bold", fontSize: "14px" }}
                  required={!isAutobook}
                  data-testid="last-name-label"
                >
                  {t("ROOSTER.COMMON.LAST-NAME", {
                    defaultValue: "Last Name"
                  })}
                </FormLabel>
                {!isAutobook ? (
                  <TextField
                    fullWidth
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Doe"
                    variant="outlined"
                    size="small"
                    onChange={handleChange}
                    value={values.lastName}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    disabled={isReschedule || isAutobook}
                    slotProps={{
                      htmlInput: {
                        "aria-describedby": errors.lastName
                          ? "lastName-error"
                          : undefined,
                        "aria-invalid": !!errors.lastName,
                        "aria-required": !isAutobook,
                        "data-testid": "last-name-input-field"
                      },
                      formHelperText: {
                        id: "lastName-error",
                        role: errors.lastName ? "alert" : undefined
                      }
                    }}
                    sx={focusRingStyles}
                    data-testid="last-name-input"
                  />
                ) : (
                  <Typography
                    fontWeight={500}
                    pt={1}
                    fontSize={16}
                    alignItems="center"
                    display="flex"
                    sx={{ gap: "5px" }}
                  >
                    <MdOutlinePerson /> {values.lastName}
                  </Typography>
                )}
              </Stack>
            </Stack>

            <Stack width="100%" data-testid="email-field-container">
              <FormLabel
                htmlFor="email"
                sx={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  ":required": { color: "red" }
                }}
                required={!isAutobook}
                data-testid="email-label"
              >
                {t("ROOSTER.COMMON.EMAIL", {
                  defaultValue: "Email"
                })}
              </FormLabel>
              {!isAutobook ? (
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  placeholder="email@example.com"
                  variant="outlined"
                  size="small"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={isReschedule || isAutobook}
                  slotProps={{
                    htmlInput: {
                      "aria-describedby": errors.email
                        ? "email-error"
                        : undefined,
                      "aria-invalid": !!errors.email,
                      "aria-required": !isAutobook,
                      "data-testid": "email-input-field"
                    },
                    formHelperText: {
                      id: "email-error",
                      role: errors.email ? "alert" : undefined
                    }
                  }}
                  sx={focusRingStyles}
                  data-testid="email-input"
                />
              ) : (
                <Typography
                  fontWeight={500}
                  pt={1}
                  fontSize={16}
                  alignItems="center"
                  display="flex"
                  sx={{ gap: "5px" }}
                >
                  <MdOutlineMailOutline size={20} /> {values.email}
                </Typography>
              )}
            </Stack>

            <Stack width="100%" data-testid="phone-field-container">
              {isAttendeePhoneRequired ? (
                <FormLabel
                  htmlFor="telephone"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    ":required": { color: "red" }
                  }}
                  required={!isAutobook}
                  data-testid="phone-label"
                >
                  <strong>
                    {t("ROOSTER.COMMON.PHONE", {
                      defaultValue: "Phone"
                    })}
                  </strong>
                </FormLabel>
              ) : (
                <FormLabel
                  htmlFor="telephone"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    ":required": { color: "red" }
                  }}
                  data-testid="phone-label"
                >
                  <strong>
                    {t("ROOSTER.COMMON.PHONE", {
                      defaultValue: "Phone"
                    })}
                  </strong>{" "}
                  {!isAutobook && (
                    <>
                      (
                      {t("ROOSTER.COMMON.OPTIONAL", {
                        defaultValue: "optional"
                      })}
                      )
                    </>
                  )}
                </FormLabel>
              )}

              {!isAutobook ? (
                <div ref={phoneContainerRef} data-testid="phone-input-wrapper">
                  <PhoneInputStyled
                    theme={mode === "light" ? "light" : "dark"}
                    preferredCountries={["us", "ca", "uk", "de", "in"]}
                    country={detectUserCountryFromCurrentTimezone(
                      "US"
                    ).toLocaleLowerCase()}
                    value={values.telephone}
                    enableSearch
                    onChange={(phone) => {
                      setFieldValue("telephone", phone);
                    }}
                    inputProps={{
                      id: "telephone",
                      name: "telephone",
                      required: isAttendeePhoneRequired,
                      "aria-label": t("ROOSTER.COMMON.PHONE", {
                        defaultValue: "Phone"
                      }),
                      "aria-describedby": errors.telephone
                        ? "telephone-error"
                        : undefined,
                      "aria-invalid": !!errors.telephone,
                      "aria-required": isAttendeePhoneRequired,
                      "data-testid": "phone-input"
                    }}
                    isValid={(inputNumber) =>
                      validatePhoneNumber(`+${inputNumber}`)
                    }
                    disabled={isAutobook}
                  />
                </div>
              ) : (
                <Typography
                  fontWeight={500}
                  pt={1}
                  fontSize={16}
                  alignItems="center"
                  display="flex"
                  sx={{ gap: "5px" }}
                >
                  <MdPhone size={20} /> {values.telephone}
                </Typography>
              )}

              {errors.telephone && !isAutobook && (
                <Typography
                  id="telephone-error"
                  role="alert"
                  variant="body1"
                  sx={{ pl: 1 }}
                  color="error.main"
                  data-testid="phone-error-message"
                >
                  {errors.telephone}
                </Typography>
              )}
            </Stack>

            <Stack
              sx={{
                display: formType === "Standalone" ? "none" : "flex"
              }}
              width="100%"
              data-testid="linkedin-field-container"
            >
              <FormLabel
                htmlFor="linkedin"
                sx={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  ":required": { color: "red" }
                }}
                data-testid="linkedin-label"
              >
                <strong>
                  {t("ROOSTER.BOOKING-FORM.LINKEDIN-URL", {
                    defaultValue: "LinkedIn URL"
                  })}
                </strong>{" "}
                (
                {t("ROOSTER.COMMON.OPTIONAL", {
                  defaultValue: "optional"
                })}
                )
              </FormLabel>
              <TextField
                fullWidth
                id="linkedin"
                name="linkedin"
                placeholder="Your LinkedIn profile URL e.g. https://www.linkedin.com/in/..."
                variant="outlined"
                size="small"
                value={values.linkedin}
                onChange={handleChange}
                error={!!errors.linkedin}
                helperText={errors.linkedin}
                slotProps={{
                  htmlInput: {
                    "aria-describedby": errors.linkedin
                      ? "linkedin-error"
                      : undefined,
                    "aria-invalid": !!errors.linkedin,
                    "data-testid": "linkedin-input-field"
                  },
                  formHelperText: {
                    id: "linkedin-error",
                    role: errors.linkedin ? "alert" : undefined
                  }
                }}
                sx={focusRingStyles}
                data-testid="linkedin-input"
              />
            </Stack>
            <Stack
              sx={{
                display: formType === "Standalone" ? "none" : "flex"
              }}
              width="100%"
              data-testid="job-ref-field-container"
            >
              <FormLabel
                htmlFor="jobRef"
                sx={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  ":required": { color: "red" }
                }}
                data-testid="job-ref-label"
              >
                <strong>
                  {t("ROOSTER.BOOKING-FORM.JOB-APPLYING-FOR", {
                    defaultValue: "Job Applying For"
                  })}
                </strong>{" "}
                (
                {t("ROOSTER.BOOKING-FORM.IF-KNOWN", {
                  defaultValue: "if known"
                })}{" "}
                -{" "}
                {t("ROOSTER.COMMON.OPTIONAL", {
                  defaultValue: "optional"
                })}
                )
              </FormLabel>
              <TextField
                fullWidth
                id="jobRef"
                name="jobRef"
                placeholder="e.g. Senior Full Stack Developer"
                variant="outlined"
                size="small"
                value={values.jobRef}
                onChange={handleChange}
                slotProps={{
                  htmlInput: {
                    "data-testid": "job-ref-input-field"
                  }
                }}
                data-testid="job-ref-input"
              />
            </Stack>
            <Stack
              sx={{
                display: formType === "Standalone" ? "none" : "flex"
              }}
              width="100%"
              data-testid="booking-note-field-container"
            >
              <FormLabel
                htmlFor="bookingNote"
                sx={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  ":required": { color: "red" }
                }}
                data-testid="booking-note-label"
              >
                <strong>
                  {t("ROOSTER.INTERVIEWER-PREFERENCES.ADDITIONAL-NOTES", {
                    defaultValue: "Additional Notes"
                  })}
                </strong>{" "}
                (
                {t("ROOSTER.COMMON.OPTIONAL", {
                  defaultValue: "optional"
                })}
                )
              </FormLabel>
              <TextField
                fullWidth
                id="bookingNote"
                name="bookingNote"
                placeholder={t(
                  "ROOSTER.BOOKING-FORM.SHARE-ANYTHING-TO-HELP-MEETING",
                  {
                    defaultValue:
                      "Please share anything that will help prepare for our meeting"
                  }
                )}
                multiline
                variant="outlined"
                value={values.bookingNote}
                onChange={handleChange}
                slotProps={{
                  htmlInput: {
                    "data-testid": "booking-note-input-field"
                  }
                }}
                data-testid="booking-note-input"
              />
            </Stack>

            {showRescheduleReasonFields && (
              <Stack
                width="100%"
                spacing={2}
                data-testid="reschedule-reason-container"
              >
                <FormControl fullWidth size="small">
                  <InputLabel
                    id="reschedule-reason-label"
                    required={isRescheduleRequired}
                  >
                    {t("ROOSTER.PUBLIC-APP.RESCHEDULE_REASON_LABEL", {
                      defaultValue: "Why are you rescheduling?"
                    })}
                  </InputLabel>
                  <Select
                    labelId="reschedule-reason-label"
                    value={selectedReasonId}
                    onChange={(e) =>
                      handleReasonChange(e.target.value as string)
                    }
                    label={t("ROOSTER.PUBLIC-APP.RESCHEDULE_REASON_LABEL", {
                      defaultValue: "Why are you rescheduling?"
                    })}
                    inputProps={{ "aria-required": isRescheduleRequired }}
                    data-testid="reschedule-reason-select"
                  >
                    {rescheduleReasons.map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {reason.reason}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {showRescheduleComment && (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={rescheduleComment}
                    onChange={(e) => handleCommentChange(e.target.value)}
                    placeholder={t(
                      "ROOSTER.PUBLIC-APP.RESCHEDULE_COMMENT_PLACEHOLDER",
                      {
                        defaultValue:
                          "Anything else you'd like to share? (optional)"
                      }
                    )}
                    helperText={`${rescheduleComment.length}/500`}
                    data-testid="reschedule-comment-field"
                  />
                )}

                {!isRescheduleRequired && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {t("ROOSTER.PUBLIC-APP.REASON_SOFT_PROMPT", {
                      defaultValue:
                        "Your feedback helps us improve the scheduling process."
                    })}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
          <Stack
            direction="column"
            width="100%"
            justifyContent="flex-end"
            data-testid="form-actions-container"
          >
            {enableTnCState && (
              <Stack
                sx={{ mx: "-10px !important", mt: "10px !important" }}
                data-testid="terms-conditions-container"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="termsAndConditions"
                      checked={values.termsAndConditions}
                      onChange={() =>
                        setFieldValue(
                          "termsAndConditions",
                          !values.termsAndConditions
                        )
                      }
                      data-testid="terms-conditions-checkbox"
                    />
                  }
                  label={
                    <Typography component="span">
                      {t(
                        "ROOSTER.BOOKING-FORM.I-AGREE-WITH-TERMS-&-CONDITION-1",
                        {
                          defaultValue: "I agree to the"
                        }
                      )}{" "}
                      &nbsp;
                      <Link
                        component="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setTnCPopupState(true);
                        }}
                        underline="always"
                        sx={{
                          fontSize: "14px !important",
                          fontWeight: "bold",
                          color: mode === "dark" ? "#90caf9" : "#1a4d7c"
                        }}
                        data-testid="terms-conditions-link"
                      >
                        {t(
                          "ROOSTER.BOOKING-FORM.I-AGREE-WITH-TERMS-&-CONDITION-2",
                          {
                            defaultValue:
                              "Terms and Conditions and Privacy Policy"
                          }
                        )}
                      </Link>
                    </Typography>
                  }
                  data-testid="terms-conditions-label"
                />
              </Stack>
            )}
            <Stack
              direction="row"
              sx={{ justifyContent: "flex-end" }}
              width="100%"
              pt={1}
              data-testid="form-buttons-container"
            >
              {!isSubmitting && (
                <Button
                  variant="text"
                  size="large"
                  onClick={() => {
                    backHandler();
                  }}
                  sx={{
                    ...(mode === "light"
                      ? {
                          ...actionBarButtonPrimaryLightStyle("text"),
                          color: "#375990"
                        }
                      : actionBarButtonPrimaryDarkStyle("text")),
                    backgroundColor: "transparent"
                  }}
                  data-testid="back-button"
                >
                  {t("ROOSTER.PUBLIC-APP.BACK", {
                    defaultValue: "Back"
                  })}
                </Button>
              )}
              <Button
                variant="text"
                size="large"
                sx={{
                  ...(mode === "light"
                    ? actionBarButtonPrimaryLightStyle("contained")
                    : actionBarButtonPrimaryDarkStyle("contained"))
                }}
                onClick={() => {
                  handleSubmit();
                }}
                disabled={
                  isSubmitting ||
                  !isValid ||
                  (enableTnCState ? !values.termsAndConditions : false) ||
                  // Until the reasons land we cannot tell whether one is
                  // required, and the dropdown is not on screen yet either.
                  rescheduleReasonsLoading ||
                  (showRescheduleReasonFields &&
                    isRescheduleRequired &&
                    !selectedReasonId)
                }
                data-testid="schedule-button"
              >
                {
                  // eslint-disable-next-line no-nested-ternary
                  isSubmitting ? (
                    <>
                      <CircularProgress
                        size="1rem"
                        sx={{
                          marginRight: "12px",
                          color: mode === "light" ? "white" : "#000000"
                        }}
                        aria-label={t("ROOSTER.ADVANCED-TAB.SCHEDULING", {
                          defaultValue: "Scheduling"
                        })}
                      />{" "}
                      <span>
                        {t("ROOSTER.ADVANCED-TAB.SCHEDULING", {
                          defaultValue: "Scheduling"
                        })}
                      </span>
                    </>
                  ) : isReschedule ? (
                    t("ROOSTER.COMMON.RESCHEDULE", {
                      defaultValue: "Reschedule"
                    })
                  ) : (
                    t("ROOSTER.COMMON.SCHEDULE", {
                      defaultValue: "Schedule"
                    })
                  )
                }
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Formik>
  );
};
